import { WppCheckbox, WppListItem, WppSelect } from '@wppopen/components-library-react'
import { FilterReducerAction, SelectFilterComponentData } from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import {
  addSelectFilter,
  removeSelectFilter,
  replaceSelectFilter,
  setAreDependantFieldsReady,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer'
import React, { useCallback, useMemo } from 'react'
import styles from './Filters.module.scss'
import { ListItemChangeEventDetail } from '@wppopen/components-library'

interface FilterSelectProps {
  filterObject: SelectFilterComponentData
  reducerDispatch: React.Dispatch<FilterReducerAction>
  isLoading: boolean
}

function FilterSelect({ filterObject, reducerDispatch, isLoading }: FilterSelectProps) {
  const {
    id,
    labelText,
    isDisabled: defaultIsDisabled,
    options,
    activeFilters,
    defaultValue,
    isMultiSelect,
    placeholder,
  } = filterObject

  const handleOptionClick = useCallback(
    (event: CustomEvent<ListItemChangeEventDetail>) => {
      event.preventDefault()
      const { value } = event.target as HTMLWppListItemElement
      const typeSafeValue = value as string
      const filter = options.find(option => option.value === typeSafeValue)
      if (!filter) return
      if (!isMultiSelect) {
        if (activeFilters[0].id === filter.id) return
        reducerDispatch(replaceSelectFilter({ filterObject, newFilterValue: filter }))
      } else {
        if (!activeFilters.find(option => option.value === typeSafeValue)) {
          reducerDispatch(addSelectFilter({ filterObject, newFilterValue: filter }))
        } else {
          reducerDispatch(removeSelectFilter({ filterObject, newFilterValue: filter }))
        }
      }
      if (id === 'clients') reducerDispatch(setAreDependantFieldsReady(false))
    },
    [activeFilters, options],
  )

  const listOptions = useMemo(() => {
    return options.map(option => {
      const isChecked =
        !!activeFilters.find(({ id }) => id === option?.id) ||
        (option?.id === defaultValue?.id && !activeFilters.length)
      return (
        <WppListItem
          checked={isChecked}
          value={option.value}
          key={option.id}
          onWppChangeListItem={handleOptionClick}
          data-testid={`${id}-li`}
          multiple={isMultiSelect || false}
          disabled={isLoading}
        >
          <WppCheckbox checked={isChecked} slot="left" data-testid={`${id}-li-checkbox`} />
          <span slot="label" className={styles.filter__liTextContent} data-testid={`${id}-li-span`}>
            {option.value}
          </span>
        </WppListItem>
      )
    })
  }, [defaultValue?.id, handleOptionClick, id, isMultiSelect, options, activeFilters, isLoading])

  let value: string | string[] = ''
  if (isMultiSelect) {
    value = activeFilters.length ? activeFilters.map(({ value }) => value) : [defaultValue?.value || '']
  } else {
    value = activeFilters[0]?.value || ''
  }

  const labelConfigText = `${labelText}${isMultiSelect && activeFilters.length ? ` (${activeFilters.length})` : ''}`
  const isDisabled = defaultIsDisabled || !options.filter(({ id }) => id !== defaultValue?.id).length

  return (
    <WppSelect
      name={id}
      labelConfig={{ text: labelConfigText }}
      required
      disabled={isDisabled}
      value={value}
      maxItemsToDisplay={4}
      data-testid={`${id}-select`}
      type={isMultiSelect ? 'multiple' : 'single'}
      placeholder={placeholder}
      showSelectAllText={false}
    >
      {listOptions}
    </WppSelect>
  )
}

export default FilterSelect
